// useDiaryResults — fetches the weekly per-driver diary view.
//
// The server returns the data already grouped by day in DiaryWeeklyResponse.
// staleTime 30s + refetchInterval 60s per the Wave B2 brief. Refetch is
// disabled while a reorder edit is in progress (the page passes
// `pauseRefetch=true` so the auto-refresh doesn't clobber unsaved state).
//
// Param-validation rule (Bug 1 fix): the `enabled` gate must reject the
// page's placeholder values (`driver_id=0`, `week_start='0000-00-00'`) so
// no HTTP fires before the page's decryption + redirect effect lands.
// `queryFn` carries an extra throw guard as a safety net — even if the
// gate is somehow bypassed, the query rejects rather than hitting the API.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useAuthStore } from '../../../store/authStore';
import type { DiaryWeeklyResponse } from '../../../types/api';

const STALE_MS = 30_000;
const REFETCH_MS = 60_000;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PLACEHOLDER_DATE = '0000-00-00';

export interface UseDiaryResultsParams {
  driverId: number;
  weekStart: string; // YYYY-MM-DD (Monday)
  /** When true, suppresses background refetch — used while user has unsaved
   *  reorder changes so a refetch doesn't overwrite their in-flight edits. */
  pauseRefetch?: boolean;
}

function paramsAreValid(driverId: number, weekStart: string): boolean {
  return (
    Number.isInteger(driverId) &&
    driverId > 0 &&
    typeof weekStart === 'string' &&
    weekStart !== PLACEHOLDER_DATE &&
    ISO_DATE_RE.test(weekStart)
  );
}

export function useDiaryResults(params: UseDiaryResultsParams) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { driverId, weekStart, pauseRefetch = false } = params;

  return useQuery<DiaryWeeklyResponse>({
    queryKey: queryKeys.diary.week(driverId, weekStart),
    queryFn: async () => {
      // Belt-and-suspenders. The `enabled` gate below should already keep
      // this path from running with placeholders, but a hard throw here
      // makes the contract explicit and prevents a misconfigured caller
      // from leaking driver_id=0 or week_start='0000-00-00' to the API.
      if (!paramsAreValid(driverId, weekStart)) {
        throw new Error(
          `useDiaryResults called with invalid params: driverId=${driverId}, weekStart=${weekStart}`,
        );
      }
      const res = await apiClient.get<DiaryWeeklyResponse>(endpoints.diary.week, {
        params: { driver_id: driverId, week_start: weekStart },
      });
      return res.data;
    },
    enabled: isAuthenticated && paramsAreValid(driverId, weekStart),
    staleTime: STALE_MS,
    refetchInterval: pauseRefetch ? false : REFETCH_MS,
    refetchOnWindowFocus: !pauseRefetch,
  });
}
